import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  Alert,
  Stack,
  Divider,
  LinearProgress,
  Avatar,
  Chip
} from "@mui/material";
import { CloudUpload, Delete, CheckCircle } from '@mui/icons-material';
import { investorApi } from "../../api/api";
import { useSelector } from "react-redux";
const AddImages = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useSelector((data) => data.themeValue?.colors) || {
    background: "#FFFFFF",
    text: "#000000",
    primary_color: "#1976D2",
    secondary_text_color: "#6E6E6E",
    normal_icon: "#1976D2",
    cardBackground: "#F5F5F5",
  };
  const propertyData = location.state || {};
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    // Validate file count
    if (files.length + selectedFiles.length > 10) {
      setErrorMessage('Maximum 10 images allowed');
      setUploadStatus('error');
      return;
    }
    // Validate file types
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      setErrorMessage('Only JPG, JPEG, and PNG files are allowed');
      setUploadStatus('error');
      return;
    }
    setErrorMessage('');
    setUploadStatus(null);
    setSelectedFiles([...selectedFiles, ...files]);
    // Create preview URLs
    const previews = files.map(file => URL.createObjectURL(file));
    setPreviewImages([...previewImages, ...previews]);
  };
  const removeImage = (index) => {
    // Revoke the object URL to prevent memory leaks
    URL.revokeObjectURL(previewImages[index]);
    const newFiles = [...selectedFiles];
    const newPreviews = [...previewImages];
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    setSelectedFiles(newFiles);
    setPreviewImages(newPreviews);
  };
  const handleUpload = async () => {
    if (!selectedFiles.length) {
      setErrorMessage('Please select at least one image');
      setUploadStatus('error');
      return;
    }
    const formData = new FormData();
    
    selectedFiles.forEach(file => {
      formData.append("Images[]", file);
    });
    if (propertyData.PropertyId) {
      formData.append("id", propertyData.PropertyId);
      formData.append("type", propertyData.PropertyType); // For property: type = 2
    } else if (propertyData.ProjectId) {
      formData.append("id", propertyData.ProjectId);
      formData.append("type", propertyData.type);  // For project: type = 1
    } else if (propertyData.propertyData.Id) {
      formData.append("id", propertyData.propertyData.Id);
      formData.append("type", propertyData.propertyData.PropertyType);  // if 3 is for apartment/villa
    }
    else if(propertyData?.propertyData?.id){
      formData.append("id",propertyData.propertyData.id);
      formData.append("type",1)
    } else {
      setErrorMessage("No valid ID found for image upload.");
      setUploadStatus("error");
      return;
    }
    // formData.append("type", type);
    setUploadStatus('uploading');
    setUploadProgress(0);
    try {
      const config = {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(progress);
        }
      };
      const response = await investorApi.addImagesById(formData, config);
      if (response.status === 200) {
        setUploadStatus('success');
        // Clear selections after successful upload
        setTimeout(() => {
          setSelectedFiles([]);
          setPreviewImages([]);
          navigate(-1); // Go back after 1.5 seconds
        }, 1500);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error("Upload error:", error);
      setErrorMessage('Failed to upload images. Please try again.');
      setUploadStatus('error');
    }
  };
  const clearAll = () => {
    // Clean up object URLs
    previewImages.forEach(url => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setPreviewImages([]);
    setUploadStatus(null);
    setErrorMessage('');
  };
  return (
    <Paper sx={{
      p: 4,
      borderRadius: 2,
      maxWidth: 800,
      mx: 'auto',
      my: 4,
      backgroundColor: theme.background
    }}>
      <Box>
        <Typography variant="h5" sx={{
          mb: 3,
          fontWeight: 600,
          color: theme.text,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <CloudUpload fontSize="large" sx={{ color: theme.normal_icon }} />
          Add Images for {propertyData?.PropertyName || "Property"}
        </Typography>
        <Divider sx={{ mb: 3, backgroundColor: theme.secondary_text_color }} />
        {/* Upload Area */}
        <Box sx={{
          border: '2px dashed',
          borderColor: 'primary.light',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          backgroundColor: 'rgba(25, 118, 210, 0.04)',
          mb: 3,
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)'
          }
        }}>
          <input
            accept="image/jpeg,image/png,image/jpg"
            type="file"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="property-image-upload"
          />
          <label htmlFor="property-image-upload">
            <Button
              variant="outlined"
              color="primary"
              component="span"
              startIcon={<CloudUpload />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Select Images
            </Button>
          </label>
          <Typography variant="body2" sx={{ mt: 2, color: theme.text }}>
            {selectedFiles.length > 0
              ? `${selectedFiles.length} image${selectedFiles.length !== 1 ? 's' : ''} selected (max 10)`
              : 'Drag & drop images here or click to browse (JPG, PNG)'}
          </Typography>
        </Box>
        {/* Status Messages */}
        {(uploadStatus === 'error' && errorMessage) && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setUploadStatus(null)}>
            {errorMessage}
          </Alert>
        )}
        {uploadStatus === 'uploading' && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              Uploading: {uploadProgress}%
            </Typography>
          </Box>
        )}
        {uploadStatus === 'success' && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            icon={<CheckCircle fontSize="inherit" />}
          >
            Images uploaded successfully! Redirecting...
          </Alert>
        )}
        {/* Selected Files Preview */}
        {previewImages.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: theme.text }}>
              Selected Images ({selectedFiles.length}/10)
            </Typography>
            <Stack spacing={2}>
              {previewImages.map((src, index) => (
                <Paper
                  key={index}
                  elevation={1}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    backgroundColor: theme.cardBackground || 'background.paper'
                  }}
                >
                  <Avatar
                    variant="rounded"
                    src={src}
                    alt={`preview-${index}`}
                    sx={{
                      width: 80,
                      height: 80,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  />
                  <Box sx={{ flexGrow: 1, textAlign: 'left' }}>
                    <Typography variant="body1" noWrap sx={{ fontWeight: 500, color: theme.text }}>
                      {selectedFiles[index].name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.secondary_text_color }}>
                      {(selectedFiles[index].size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                  <Chip
                    label="Ready"
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                  <IconButton
                    onClick={() => removeImage(index)}
                    size="small"
                    sx={{ color: 'error.main' }}
                  >
                    <Delete />
                  </IconButton>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          {selectedFiles.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              onClick={clearAll}
              disabled={uploadStatus === 'uploading'}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Clear All
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || uploadStatus === 'uploading'}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }
            }}
          >
            {uploadStatus === 'uploading' ? 'Uploading...' : `Upload Images`}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
};
export default AddImages;